// Customer Portal console form model (Changes #100781-#100784).
//
// One form for the whole console: every tab edits a slice of it and one Save
// sends only the keys that changed to PUT /portalprofile/update. Flags go as
// 1/0, blank strings as null, type id lists as arrays and the two column
// pickers as ordered arrays of field keys.

export const MODE = { OFF: "0", READ: "1", SUBMIT: "2" };

export const MODE_LABELS = {
  [MODE.OFF]: "Off",
  [MODE.READ]: "Read only",
  [MODE.SUBMIT]: "Read & submit",
};

const TEXT = [
  "Name", "Description", "NotificationEmailAddress",
  // #100781 branding
  "PortalTitle", "WelcomeText", "LogoUrl", "FaviconUrl", "FontFamily",
  "TextColour", "ButtonBackgroundColour", "ButtonTextColour",
  "HeaderBackgroundColour", "HeaderTextColour", "PageBackgroundColour",
  // #100783 / #100784 labels
  "JobsLabelPlural", "JobsLabelSingular", "JobsRaiseButtonText",
  "TasksLabelPlural", "TasksLabelSingular", "TasksRaiseButtonText",
];

const INT = [
  "Priority", "LogoHeightPx", "JobsMode", "TasksMode",
  "JobsDefaultJobType_Id", "JobsDefaultJobStatus_Id", "JobsDefaultJobPriority_Id", "JobsDefaultStaff_Id",
  "TasksDefaultTaskType_Id", "TasksDefaultTaskStatus_Id", "TasksDefaultTaskPriority_Id", "TasksDefaultStaff_Id",
  "TasksDefaultOrganisationDepartment_Id",
];

const FLAG = [
  "FlagActive", "FlagCanViewFinancials", "FlagCanViewContractUsage", "FlagHidePoweredBy",
  "JobsFlagCanRaise", "JobsFlagCanComment", "JobsFlagCanUpload", "JobsFlagShowCompleted",
  "JobsFlagAskSiteLocation", "JobsFlagAskReference",
  "TasksFlagCanRaise", "TasksFlagCanComment", "TasksFlagCanUpload", "TasksFlagShowCompleted",
  "TasksFlagAllowLinkJob",
];

const ID_LIST = ["JobsJobTypeIds", "TasksTaskTypeIds"];
const KEY_LIST = ["fields", "task_fields"];

const FLAG_SET = new Set(FLAG);
const INT_SET = new Set(INT);
const ID_LIST_SET = new Set(ID_LIST);
const KEY_LIST_SET = new Set(KEY_LIST);

export const FORM_KEYS = [...TEXT, ...INT, ...FLAG, ...ID_LIST, ...KEY_LIST];

const on = (v) => v !== null && v !== undefined && String(v) === "1";

export const parseIds = (v) =>
  (Array.isArray(v) ? v : String(v ?? "").split(","))
    .map((x) => String(x).trim())
    .filter((x) => /^\d+$/.test(x) && x !== "0");

// Mirrors CustomerPortalAccessLibrary::normaliseProfile so a portal saved
// before the v2 deltas shows what customers really get.
export const jobsModeOf = (row) => {
  if (row && row.JobsMode !== undefined && row.JobsMode !== null && row.JobsMode !== "") {
    return String(row.JobsMode);
  }
  return on(row?.FlagCanComment) || on(row?.FlagCanUploadDocument) ? MODE.SUBMIT : MODE.READ;
};

export const tasksModeOf = (row) =>
  row && row.TasksMode !== undefined && row.TasksMode !== null && row.TasksMode !== ""
    ? String(row.TasksMode)
    : MODE.OFF;

// Flags whose blank value means "on" in the portal.
const DEFAULT_ON = new Set(["JobsFlagAskSiteLocation", "JobsFlagAskReference", "TasksFlagAllowLinkJob"]);

const flagOf = (row, k) => {
  const v = row?.[k];
  if (k === "JobsFlagCanComment" && !(k in (row || {}))) return on(row?.FlagCanComment);
  if (k === "JobsFlagCanUpload" && !(k in (row || {}))) return on(row?.FlagCanUploadDocument);
  if (v === null || v === undefined || v === "") return DEFAULT_ON.has(k);
  return on(v);
};

const fieldKeys = (list) =>
  (list || [])
    .map((f) => (typeof f === "string" ? f : f?.FieldKey))
    .filter(Boolean);

export const fromProfile = (row) => {
  const out = {};
  TEXT.forEach((k) => {
    out[k] = row?.[k] ?? "";
  });
  INT.forEach((k) => {
    out[k] = row?.[k] === null || row?.[k] === undefined ? "" : String(row[k]);
  });
  out.JobsMode = jobsModeOf(row);
  out.TasksMode = tasksModeOf(row);
  FLAG.forEach((k) => {
    out[k] = flagOf(row, k);
  });
  ID_LIST.forEach((k) => {
    out[k] = parseIds(row?.[k]);
  });
  out.fields = fieldKeys(row?.fields);
  out.task_fields = fieldKeys(row?.task_fields);
  return out;
};

const same = (k, a, b) => {
  if (ID_LIST_SET.has(k)) return [...a].sort().join(",") === [...b].sort().join(",");
  if (KEY_LIST_SET.has(k)) return a.join(",") === b.join(",");
  return a === b;
};

export const changedKeys = (form, original) =>
  FORM_KEYS.filter((k) => !same(k, form[k], original[k]));

export const buildPatch = (id, form, keys) => {
  const patch = { Id: id };
  keys.forEach((k) => {
    const v = form[k];
    if (FLAG_SET.has(k)) patch[k] = v ? 1 : 0;
    else if (ID_LIST_SET.has(k)) patch[k] = v.map(Number);
    else if (KEY_LIST_SET.has(k)) patch[k] = [...v];
    else if (INT_SET.has(k)) patch[k] = v === "" ? null : Number(v);
    else patch[k] = String(v).trim() === "" ? null : v;
  });
  return patch;
};
